<template>
  <div class="container dashboard">
    <div class="session-outcomes-report">
      <div class="col-12 title">
        <h1>Session Outcomes</h1>
        <div class="sub">
          by User
        </div>
      </div>
      <div class="row">
        <div class="card filters">
          <div class="table-filters">
            <date-filters
              :from="from"
              :through="through"
              @change="filterUpdate"
            />
            <outcomes-filter :value="selectedOutcomes" @input="selectedOutcomes = $event; search()" />
            <custom-tags-input :value="selectedTags" @input="selectedTags = $event; search()" />
            <outcomes-search-bar :has-sso="hasSsoIds" @set-search="setSearch" />
            <button type="button" class="bm-button" :disabled="loading" @click="search">
              <font-awesome-icon
                :icon="['fas', 'sync-alt']"
                class="icon alt"
              />
            </button>
          </div>
        </div>
      </div>
      <div>
        <div class="row">
          <div class="card col-12 p-0">
            <font-awesome-icon
              v-if="loading"
              :icon="['fas', 'spinner']"
              class="icon alt spinner"
              aria-hidden="true"
              :spin="true"
            />
            <div v-else-if="noRecords" class="empty">
              No Results
            </div>
            <template v-else>
              <b-table class="outcomes-table text-center" :fields="fields" :items="outcomes" head-variant="dark" responsive small>
                <template #cell(show_details)="row">
                  <b-button size="sm" @click="row.detailsShowing ? row.toggleDetails() : row.toggleDetails(); getDetails(row.item.userId);">
                    <span id="hidden-text">{{ row.detailsShowing ? 'Hide' : 'View' }} Outcomes</span>
                    <font-awesome-icon
                      id="hidden-icon"
                      :icon="[
                        'fas',
                        row.detailsShowing ? 'xmark' : 'list'
                      ]"
                    />
                  </b-button>
                </template>
                <template #row-details="row">
                  <b-card class="details-card">
                    <b-row>
                      <b-col class="inner-table">
                        <Loader v-if="!row.item.details && detailsLoading" style="width: 100%;" />
                        <div v-else-if="!row.item.details.length" class="empty">No Results</div>
                        <outcome-details-table v-else :details="row.item.details"></outcome-details-table>
                      </b-col>
                    </b-row>
                  </b-card>
                </template>
                <template #custom-foot>
                  <b-tr>
                    <b-td>{{ outcomes.length }} Users  </b-td>
                    <b-td></b-td>
                    <b-td v-if="hasSsoIds"></b-td>
                    <b-td v-if="includeUpcomingSessions">{{ total.upcoming }}</b-td>
                    <b-td>{{ total.successful }}</b-td>
                    <b-td>{{ total.noShow }}</b-td>
                    <b-td>{{ total.lateCancel }}</b-td>
                    <b-td>
                      <b-button
                        v-if="outcomes && outcomes.length"
                        size="sm"
                        @click="exportToCsv"
                      >
                        <span id="hidden-text">Download </span>
                        <font-awesome-icon
                          :icon="{
                            prefix:'fas',
                            iconName: 'download'
                          }"
                        />
                      </b-button>
                    </b-td>
                  </b-tr>
                </template>
              </b-table>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateFilters from './components/DateFilters.vue';
import OutcomeDetailsTable from './components/OutcomeDetailsTable.vue';
import OutcomesFilter from './components/OutcomesFilter.vue';
import OutcomesSearchBar from './components/OutcomesSearchBar.vue';
import SessionClient from '@/api/SessionClient';
import CustomTagsInput from '../../dashboard/components/CustomTagsInput.vue';
import CustomUserTags from '../../dashboard/store/custom-user-tags';
import Loader from '@/components/Loader.vue';
import downloadCsv from '../../../helpers/mixins/download-csv'
import moment from 'moment';

export default {
  name: 'SessionOutcomes',
  components: {
    Loader,
    DateFilters,
    OutcomesFilter,
    CustomTagsInput,
    OutcomeDetailsTable,
    OutcomesSearchBar
  },
  mixins: [downloadCsv],
  data() {
    return {
      client: new SessionClient(this.$http),
      schoolId: null,
      from: moment().startOf('day').subtract(14, 'days'),
      through: moment(),
      selectedOutcomes: [],
      loading: false,
      outcomes:[],
      customTags: new CustomUserTags(),
      selectedTags: [],
      selectedRange: 'pickRange',
      searches: '',
      total: {}
    }
  },
  provide() {
    return {
      customUserTags: this.customTags
    }
  },
  computed:{
    fields() {
        return [
                { key: 'lastName', label: 'Name', sortable: true, formatter: 'fullName', class: 'name' },
                { key: 'email', label: 'Email', sortable: true, sortKey: 'ByEmail', class: 'email' },
                this.hasSsoIds ? { key: 'ssoId', label: 'SSO ID', sortable: true, sortKey: 'BySsoId', class: 'ssoId' } : '',
                this.includeUpcomingSessions ? { key: 'upcomingCount', label: 'Upcoming', sortable: true, sortKey: 'ByUpcoming' } : '',
                { key: 'successfulCount', label: 'Successful', sortable: true, sortKey: 'BySuccessful' },
                { key: 'noShowCount', label: 'No Shows', sortable: true, sortKey: 'ByNoShows' },
                { key: 'lateCancelCount', label: 'Late Cancels', sortable: true, sortKey: 'ByLateCancels' },
                { key: 'show_details', label: '' }
              ];
    },
    hasSsoIds() {
      return this.outcomes.some(x => !!x.ssoId)
    },
    includeUpcomingSessions() {
      return this.selectedRange === 'all' ||
            (this.selectedRange === 'pickRange' &&
             this.outcomes.some(x => x.upcomingCount && x.upcomingCount > 0));
    },
    noRecords() {
      return !this.outcomes.length;
    },
  },
  mounted() {
    this.schoolId = this.$store.getters.currentUser.school.id;
    this.customTags.initialize(this.$http, this.schoolId);
    this.search();
  },
  methods:{
    fullName(value, key, item) {
      return `${item.firstName} ${item.lastName}`
    },
    async copyText(text) {
      if (!navigator || !navigator.clipboard) return;

      try {
        await navigator.clipboard.writeText(text);
        this.$store.commit('setGlobalAlert', {
          msg: 'Copied to clipboard.',
        });
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed copying to clipboard.',
          error:true
        });
      }
    },
    async search() {
      try {
        this.sortBy = null;
        this.loading = true;

        const response = await this.client.getSessionOutcomeMetrics(
          this.schoolId,
          this.from ? this.from.toISOString() : undefined,
          this.through ? this.through.toISOString() : undefined,
          this.selectedOutcomes.length ? this.selectedOutcomes : undefined,
          this.selectedTags.length ? this.selectedTags : undefined,
          this.getSearchParameters());
        this.outcomes = response.outcomes;
        this.calculateTotals();
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Something went wrong searching. Please try again. If the problem persists, contact support.',
          error:true
        });
      } finally {
        this.loading = false;
      }
    },
    filterUpdate(event) {
      this.selectedRange = event.selected;
      this.from = null;
      this.through = null;

      if (event.from) this.from = event.from;
      if (event.through) this.through = event.through;
      this.search();
    },
    setSearch(value) {
      this.searches = value;
      this.search();
    },
    async getDetails(userId) {
      try {
        this.detailsLoading = true;
        const response = await this.client.getSessionOutcomesDrillDownReport(
          this.schoolId,
          userId,
          this.from ? this.from.toISOString() : null,
          this.through ? this.through.toISOString() : null);

        const index = this.outcomes.findIndex(obj => obj.userId === userId);

        if (index >= 0) {
          const record = this.outcomes.splice(index, 1)[0];
          record.details = response.outcomes.map(x => ({ ...x, outcomeType: this.addSpacesBetweenOutcomeTypeNames(x.outcomeType) }));
          this.outcomes.splice(index, 0, record);
        }
    } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Something went wrong. Please try again. If the problem persists, contact support.',
          error:true
        });
      } finally {
        this.detailsLoading = false;
      }
    },
    getSearchParameters() {
      if (this.searches) {
        return this.searches
          .split(/\s+/) // match all whitespace
          .filter(Boolean) // remove empty
        }
      return undefined;
    },
    addSpacesBetweenOutcomeTypeNames(string) {
      return string.replace(/([a-z])([A-Z])/g, '$1 $2');
    },
    calculateTotals() {
      this.total.upcoming = this.outcomes.reduce((total, item) => total + item.upcomingCount, 0);
      this.total.successful = this.outcomes.reduce((total, item) => total + item.successfulCount, 0);
      this.total.noShow = this.outcomes.reduce((total, item) => total + item.noShowCount, 0);
      this.total.lateCancel = this.outcomes.reduce((total, item) => total + item.lateCancelCount, 0);
    },
    exportToCsv() {
      const mappers = [
        { header: 'User', mapper: x => x.email },
        { header: 'Upcoming', mapper: x => x.upcomingCount },
        { header: 'Successful', mapper: x => x.successfulCount },
        { header: 'No Show', mapper: x => x.noShowCount },
        { header: 'Late Cancel', mapper: x => x.lateCancelCount },
      ];
      if(this.hasSsoIds)
        mappers.splice(1, 0, { header: 'SsoId', mapper: x => x.ssoId || '' });

      let fileName = '';
      if((this.selectedOutcomes.length && this.selectedOutcomes.length < 4)
        || (this.selectedTags.length))
        fileName = 'filtered_';
      
      fileName += 'session_outcome_report';

      if(this.from)
        fileName += `_from_${this.from.format('YYYYMMDD')}`;

      if(this.through)
        fileName += `_through_${this.through.format('YYYYMMDD')}`;
      else
        fileName += `_through_${moment.tz(moment(), moment.tz.guess()).toISOString(true)}`;
      
      fileName += '.csv';

      const csv = this.toCsv(this.outcomes, mappers);
      this.downloadCsv(csv, fileName)
    }
  }
}

</script>

<style lang="scss">

.session-outcomes-report {
  display: flex;
  flex-direction: column;
  max-width: 100%;

  .outcomes-table {
    border-radius:5px;
    margin-bottom: 0;

    @media screen and (max-width: 1200px) {
      #hidden-text {
        display: none;
      }
    }
    @media screen and (min-width: 1201px) {
      #hidden-icon {
        display: none;
      }
    }
    td {
      vertical-align: middle;
      border-top: 1px solid $dark-blue;
    }

    .b-table-has-details {
      background-color: rgba(0, 0, 0, 0.05);
    }
    .b-table-details {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .thead-dark th{
      background-color: $blue !important;
      border-color: $blue !important;
    }
    tfoot {
      background-color: $blue !important;
      color: white;
      font-weight: 700;
      font-size: 1.15rem;
    }
  }

  .empty {
    min-height: 280px;
    justify-content: center;
    font-size: 16px;
    margin: 2rem;
    width: calc(100% - 4rem);
  }
  .spinner {
    margin-top: 50px;
    align-self: center;
    font-size: 50px;
  }

  .title {
      height: auto;
    display: flex;
    flex-direction: column;
    align-items: left;
    padding: 0;
    line-height: 1.05;
    margin-bottom: 1rem;

    h1 {
      color: $white;
      font-family: $sans;
      font-size: 1.33rem;
      display: inline-block;
      margin: 0 0 0.3rem 0;
      line-height: inherit;
      letter-spacing: 0.05rem;
    }

    .sub {
      font-size: 16px;
      font-weight: 500;
      color: #bfc0c7;
    }
  }
  .card {
    color: $black;
    min-height: 100px;
  }
  h1 {
    color:$black;
  }
  .card.filters {
    display: flex;
    width: 100%;
    margin: 1rem 0 1rem 0;
    padding: 15px;
    min-height: 0px;
    .right {
      display: flex;
      flex-direction: column;
    }
    .table-filters {
      display:flex;
      flex-flow: row wrap;
      gap: 0.5rem;
    }
  }

  .bm-button.wide {
    width: 500px;
    align-self: center;
  }
  .bm-button {
    margin-left: auto;
    color-scheme: light;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #d1d1d1;
    padding: 10px;
    height: 46px;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    text-align: center;
    cursor: pointer;
  }

  .details-card {
    min-height: 0;
    margin-right: 2.5%;
    margin-left: 2.5%;
    margin-bottom: 1rem;
    border: 0px;
    .card-body {
      padding-top: 0;
      padding-bottom: 0;
       background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .ssoId {
    max-width: 150px;
  }

  .email {
    max-width: 300px;
  }

  .name {
    max-width: 200px;
  }

}
</style>
